import { action, computed, observable } from 'mobx';
import { computeMetricsSummaries, MetricsGranularity } from '../lib/metricsUtils';
import { ZipApi } from '../lib/ZipApi';
import { DataSyncMetricsBucket, DataSyncMetricsSummary } from '../types/DataSyncMetrics';

const METRICS_CACHE_TTL_MS = 60 * 1000;

interface MetricsQuery {
  trackerId: string;
  fromEpoch: number;
  toEpoch: number;
  granularity: MetricsGranularity;
}

interface MetricsQueryCacheEntry {
  summary?: DataSyncMetricsSummary;
  buckets: DataSyncMetricsBucket[];
  fetchedAtMs: number;
}

export class DataSyncMetricsStore {

  @computed
  public get summaries(): Map<string, DataSyncMetricsSummary> {
    return this._summaries;
  }

  @computed
  public get bucketsByDataSync(): Map<string, DataSyncMetricsBucket[]> {
    return this._bucketsByDataSync;
  }

  private static getQueryKey(query: MetricsQuery, datasyncId: string): string {
    return `${query.trackerId}:${query.fromEpoch}:${query.toEpoch}:${query.granularity}:${datasyncId}`;
  }

  private static isCacheFresh(entry: MetricsQueryCacheEntry | undefined, nowMs: number): boolean {
    if (!entry) {
      return false;
    }

    return (nowMs - entry.fetchedAtMs) < METRICS_CACHE_TTL_MS;
  }
  @observable public loading = false;
  @observable private _summaries = new Map<string, DataSyncMetricsSummary>();
  @observable private _bucketsByDataSync = new Map<string, DataSyncMetricsBucket[]>();
  private _cacheByQueryKey = new Map<string, MetricsQueryCacheEntry>();

  @action
  public clear() {
    this._summaries = new Map();
    this._bucketsByDataSync = new Map();
    this._cacheByQueryKey = new Map();
  }

  @action
  public async fetchMetrics(
    trackerId: string,
    datasyncIds: string[],
    options?: {
      fromEpoch?: number;
      toEpoch?: number;
      granularity?: MetricsGranularity;
    }
  ) {
    if (!trackerId || datasyncIds.length === 0) {
      return;
    }

    const toEpoch = options?.toEpoch || Math.floor(Date.now() / 1000);
    const fromEpoch = options?.fromEpoch || (toEpoch - 7 * 24 * 60 * 60);
    const granularity = options?.granularity || 'METRICS_GRANULARITY_DAILY';
    const query: MetricsQuery = {
      trackerId,
      fromEpoch,
      toEpoch,
      granularity,
    };
    const nowMs = Date.now();

    const idsToFetch: string[] = [];
    datasyncIds.forEach((datasyncId) => {
      const cacheKey = DataSyncMetricsStore.getQueryKey(query, datasyncId);
      const cached = this._cacheByQueryKey.get(cacheKey);

      if (cached && DataSyncMetricsStore.isCacheFresh(cached, nowMs)) {
        this.updateLatestData(datasyncId, cached.buckets, cached.summary);
      } else {
        idsToFetch.push(datasyncId);
      }
    });

    if (idsToFetch.length === 0) {
      return;
    }

    this.loading = true;
    try {
      const response = await ZipApi.getDataSyncMetrics(
        trackerId,
        idsToFetch,
        fromEpoch,
        toEpoch,
        granularity
      );
      const buckets = response.metrics_buckets || [];

      const groupedBuckets = new Map<string, DataSyncMetricsBucket[]>();
      idsToFetch.forEach((datasyncId) => groupedBuckets.set(datasyncId, []));
      buckets.forEach((bucket) => {
        const entries = groupedBuckets.get(bucket.datasync_id) || [];
        entries.push(bucket);
        groupedBuckets.set(bucket.datasync_id, entries);
      });

      const newSummaries = computeMetricsSummaries(buckets, {
        fromEpoch,
        toEpoch,
        granularity,
      });

      const fetchedAtMs = Date.now();
      idsToFetch.forEach((datasyncId) => {
        const grouped = groupedBuckets.get(datasyncId) || [];
        const summary = newSummaries.get(datasyncId);

        this.updateLatestData(datasyncId, grouped, summary);
        this._cacheByQueryKey.set(DataSyncMetricsStore.getQueryKey(query, datasyncId), {
          summary,
          buckets: grouped,
          fetchedAtMs,
        });
      });
    } finally {
      this.loading = false;
    }
  }

  public getSummary(datasyncId: string, options?: MetricsQuery): DataSyncMetricsSummary | undefined {
    if (options) {
      const cacheKey = DataSyncMetricsStore.getQueryKey(options, datasyncId);
      return this._cacheByQueryKey.get(cacheKey)?.summary;
    }

    return this._summaries.get(datasyncId);
  }

  public getBuckets(datasyncId: string, options?: MetricsQuery): DataSyncMetricsBucket[] {
    if (options) {
      const cacheKey = DataSyncMetricsStore.getQueryKey(options, datasyncId);
      return this._cacheByQueryKey.get(cacheKey)?.buckets || [];
    }

    return this._bucketsByDataSync.get(datasyncId) || [];
  }

  private updateLatestData(
    datasyncId: string,
    buckets: DataSyncMetricsBucket[],
    summary?: DataSyncMetricsSummary
  ) {
    this._bucketsByDataSync.set(datasyncId, buckets);
    if (summary) {
      this._summaries.set(datasyncId, summary);
    }
  }
}

export const dataSyncMetricsStore = new DataSyncMetricsStore();
